import { StateCreator } from "zustand";

/**
 * Public profile sheet slice state.
 *
 * NOTE: This slice only holds client-only UI state (sheet open/closed).
 * Profile data comes from the server via React Query.
 */
export type PublicProfileSliceState = {
  /** Whether the public profile preview sheet is open */
  isPublicProfileSheetOpen: boolean;
};

/**
 * Public profile sheet slice actions.
 */
export type PublicProfileSliceActions = {
  openPublicProfileSheet: () => void;
  closePublicProfileSheet: () => void;
  togglePublicProfileSheet: () => void;
};

/**
 * Full public profile slice type.
 */
export type PublicProfileSlice = PublicProfileSliceState &
  PublicProfileSliceActions;

/**
 * Initial state for public profile slice.
 */
export const initialPublicProfileState: PublicProfileSliceState = {
  isPublicProfileSheetOpen: false,
};

/**
 * Creates the public profile slice for Zustand store.
 *
 * @example
 * ```typescript
 * const useStore = create<PublicProfileSlice>()((...a) => ({
 *   ...createPublicProfileSlice(...a),
 * }));
 *
 * // In a component:
 * const isOpen = useStore((s) => s.isPublicProfileSheetOpen);
 * const open = useStore((s) => s.openPublicProfileSheet);
 * ```
 */
export const createPublicProfileSlice: StateCreator<
  PublicProfileSlice,
  [],
  [],
  PublicProfileSlice
> = (set) => ({
  ...initialPublicProfileState,

  openPublicProfileSheet: () => set({ isPublicProfileSheetOpen: true }),

  closePublicProfileSheet: () => set({ isPublicProfileSheetOpen: false }),

  togglePublicProfileSheet: () =>
    set((state) => ({ isPublicProfileSheetOpen: !state.isPublicProfileSheetOpen })),
});

// --- Selectors ---
export const publicProfile = {
  sheet: (s: PublicProfileSlice) => ({
    isOpen: s.isPublicProfileSheetOpen,
    open: s.openPublicProfileSheet,
    close: s.closePublicProfileSheet,
    toggle: s.togglePublicProfileSheet,
  }),
};
